import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { fmtAgo } from "../lib/format";
import { FeedbackForm } from "./FeedbackForm";

/**
 * Feedback for THIS run, on the run's own page — both the form and whatever has already been said
 * and done. Shown here rather than only on /feedback because this is where the author is when
 * they remember what they meant, and because seeing that a previous entry was acted on is what
 * makes anyone write a second one.
 */
export function RunFeedback({ runId, deviceId, prompt }: { runId: string; deviceId?: string | null; prompt?: string | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["feedback", runId], queryFn: () => api.feedback(runId) });
  const entries = q.data ?? [];

  return (
    <div style={{ marginBottom: 12 }}>
      {entries.map((e) => (
        <div key={e.id} className="panel" style={{ marginBottom: 8 }}>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline" }}>
            <strong>{e.intention}</strong>
            <span className="muted" style={{ fontSize: 12 }}>
              {e.author} · {fmtAgo(e.created_ts)}
            </span>
          </div>
          {e.what_happened && <div style={{ marginTop: 4 }}>{e.what_happened}</div>}
          {e.what_went_wrong && (
            <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
              their guess: {e.what_went_wrong}
            </div>
          )}
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            {e.handling.length === 0
              ? "nothing done yet"
              : e.handling.map((h) => `${h.author}: ${h.body}`).join(" · ")}
          </div>
        </div>
      ))}

      {open ? (
        <FeedbackForm
          runId={runId}
          deviceId={deviceId}
          prompt={prompt}
          onSaved={() => {
            setOpen(false);
            qc.invalidateQueries({ queryKey: ["feedback"] });
          }}
        />
      ) : (
        <button onClick={() => setOpen(true)}>tell us about this run</button>
      )}
    </div>
  );
}
